import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import { Trash, ArrowCounterClockwise, Warning, Package, UserCircle, FileText, Broom } from '@phosphor-icons/react';

const ENTITY_META = {
  artikel: { label: 'Artikelen', icon: Package, color: '#002FA7' },
  klant: { label: 'Klanten', icon: UserCircle, color: '#059669' },
  document: { label: 'Documenten', icon: FileText, color: '#7C3AED' },
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

export default function PrullenbakPanel() {
  const [data, setData] = useState({ items: [], counts: {}, ttl_dagen: 30 });
  const [filter, setFilter] = useState('alle');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [geenToegang, setGeenToegang] = useState(false);

  const load = () => {
    setBusy(true);
    API.get('/prullenbak')
      .then(r => { setData(r.data || { items: [], counts: {}, ttl_dagen: 30 }); setGeenToegang(false); })
      .catch(e => {
        if (e.response?.status === 403) {
          setGeenToegang(true);
        } else {
          setToast({ text: 'Laden mislukt', kind: 'err' });
        }
        setData({ items: [], counts: {}, ttl_dagen: 30 });
      })
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const herstel = async (p) => {
    if (!window.confirm(`${p.label}\n\nTerugzetten naar de originele lijst?`)) return;
    try {
      await API.post(`/prullenbak/${p.id}/herstel`);
      setToast({ text: `✓ Hersteld: ${p.label}`, kind: 'ok' });
      load();
    } catch (e) {
      setToast({ text: e.response?.data?.detail || 'Herstellen mislukt', kind: 'err' });
    }
  };

  const permDelete = async (p) => {
    if (!window.confirm(`${p.label}\n\nDEFINITIEF verwijderen? Dit kan niet meer hersteld worden.`)) return;
    try {
      await API.delete(`/prullenbak/${p.id}`);
      setToast({ text: 'Definitief verwijderd', kind: 'ok' });
      load();
    } catch (e) {
      setToast({ text: e.response?.data?.detail || 'Verwijderen mislukt', kind: 'err' });
    }
  };

  const leegmaken = async () => {
    if (!window.confirm(`Volledige prullenbak (${data.items.length} items) DEFINITIEF verwijderen? Niets kan hersteld worden na deze actie.`)) return;
    try {
      const r = await API.post('/prullenbak/leegmaken');
      setToast({ text: `✓ ${r.data.verwijderd || 0} items definitief verwijderd`, kind: 'ok' });
      load();
    } catch (e) {
      setToast({ text: e.response?.data?.detail || 'Leegmaken mislukt', kind: 'err' });
    }
  };

  const zichtbaar = useMemo(() => {
    if (filter === 'alle') return data.items;
    return data.items.filter(i => i.entity === filter);
  }, [data.items, filter]);

  return (
    <div data-testid="prullenbak-panel">
      {geenToegang && (
        <div className="mb-4 p-4 border-2 border-red-200 bg-red-50 text-red-800" data-testid="prullenbak-geen-toegang">
          <div className="inline-flex items-center gap-2 font-bold mb-1">
            <Warning size={18} weight="fill" /> Geen toegang tot de prullenbak
          </div>
          <div className="text-xs">Enkel gebruikers met de rol <strong>admin</strong> kunnen verwijderde items beheren en herstellen.</div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-gray-900 inline-flex items-center gap-2">
            <Trash size={20} weight="duotone" className="text-[#DC2626]" />
            Prullenbak
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Verwijderde items blijven <strong>{data.ttl_dagen} dagen</strong> bewaarbaar. Daarna worden ze automatisch definitief verwijderd.
          </p>
        </div>
        {data.items.length > 0 && (
          <button className="btn btn-danger btn-sm inline-flex items-center gap-1.5" onClick={leegmaken} data-testid="prullenbak-leegmaken">
            <Broom size={14} /> Volledig leegmaken
          </button>
        )}
      </div>

      {/* Tabs per entity */}
      <div className="flex gap-1 mb-3 border-b border-gray-200">
        <button
          className={`px-3 py-2 text-sm font-bold transition-all ${filter === 'alle' ? 'text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => setFilter('alle')}
          data-testid="prullenbak-tab-alle"
        >
          Alles ({data.items.length})
        </button>
        {Object.entries(ENTITY_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = data.counts[key] || 0;
          return (
            <button
              key={key}
              className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 transition-all ${filter === key ? 'border-b-2' : 'text-gray-500 hover:text-gray-800'}`}
              style={filter === key ? { color: meta.color, borderColor: meta.color } : undefined}
              onClick={() => setFilter(key)}
              data-testid={`prullenbak-tab-${key}`}
            >
              <Icon size={14} weight="duotone" /> {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {busy && <div className="text-center py-6 text-sm text-gray-500">Laden...</div>}

      {!busy && zichtbaar.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-500 border border-dashed border-gray-300">
          <Trash size={28} className="mx-auto text-gray-300 mb-2" weight="duotone" />
          Prullenbak is leeg. Verwijderde items verschijnen hier.
        </div>
      )}

      {!busy && zichtbaar.length > 0 && (
        <div className="border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600 font-bold">
              <tr>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2">Verwijderd op</th>
                <th className="text-left px-3 py-2">Door</th>
                <th className="text-left px-3 py-2">Auto-verwijdering</th>
                <th className="text-right px-3 py-2">Acties</th>
              </tr>
            </thead>
            <tbody>
              {zichtbaar.map(p => {
                const meta = ENTITY_META[p.entity] || { label: p.entity, icon: Warning, color: '#6B7280' };
                const Icon = meta.icon;
                const dagen = daysLeft(p.verloopt_op);
                const urgent = dagen !== null && dagen <= 3;
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`prullenbak-rij-${p.id}`}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 border" style={{ color: meta.color, borderColor: meta.color, background: `${meta.color}10` }}>
                        <Icon size={11} weight="fill" /> {meta.label.slice(0, -2) || meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium truncate max-w-[340px]" title={p.label}>{p.label}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(p.verwijderd_op)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{p.verwijderd_door || '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] font-bold px-1.5 py-0.5 ${urgent ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                        {dagen === 0 ? 'VANDAAG' : dagen === null ? '-' : `nog ${dagen}d`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => herstel(p)}
                        className="btn btn-primary btn-sm inline-flex items-center gap-1 font-bold"
                        title="Terugzetten naar originele lijst"
                        data-testid={`prullenbak-herstel-${p.id}`}
                      >
                        <ArrowCounterClockwise size={12} weight="bold" /> Herstel
                      </button>
                      <button
                        onClick={() => permDelete(p)}
                        className="ml-1 p-1.5 hover:bg-red-100 text-red-600"
                        title="Definitief verwijderen"
                        data-testid={`prullenbak-perm-del-${p.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 px-4 py-3 shadow-2xl font-bold text-sm"
          style={{ background: toast.kind === 'ok' ? '#16A34A' : toast.kind === 'warn' ? '#F59E0B' : '#DC2626', color: '#fff', minWidth: 260 }}
          data-testid="prullenbak-toast"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
